import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import { useCallback, useEffect, useMemo, useState } from "react";
import * as yup from "yup";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";

// Material Dashboard 2 React example components
import { yupResolver } from "@hookform/resolvers/yup";
import { adminResetPassword, createStaff, getStaffById, updateStaff } from "api/staff";
import HeaderCard from "components/DataTable/HeaderCard";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import useGlobalStore from "store/useGlobalStore";
import Swal from "sweetalert2";
import { removeEmptyFields } from "utils/helper";
import { parentRoute } from ".";
import UserForm from "./form";

const staffSchema = yup.object({
    sf_full_name: yup.string().trim().required("Full name is required").max(100, "Max 100 characters"),
    sf_username: yup.string().trim().required("Username is required").max(100, "Max 100 characters"),
    sf_start_working_date: yup.string().nullable(),
    sf_role: yup.string().required("Role is required"),
    sf_description: yup.string().max(200, "Max 200 characters"),
    sf_status: yup.mixed().when('isAddStaff',{
        is: false,
        then: (schema) => schema.required("Status is required"), 
        otherwise: (schema) => schema.nullable()
    }),
});

const AddStaff = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { setStaffs, setPrevSelectedRow } = useGlobalStore();
    const [formData, setFormData] = useState();
    const [formErrors, setFormErrors] = useState({});
    const [passwordFormData, setPasswordFormData] = useState();
    const [isLoading, setIsLoading] = useState(false);
    const id = Number(location.pathname.split('/')[2]);

    const isAddStaff = useMemo(() =>
        location.pathname.includes(`${parentRoute}/add-staff`),
        [location.pathname]
    );

    const {
        trigger,
        setValue,
        formState: { errors: rhfErrors },
    } = useForm({
        resolver: yupResolver(staffSchema),
        mode: "onSubmit",
        defaultValues: {
            sf_full_name: "",
            sf_username: "",
            sf_start_working_date: "",
            sf_role: "",
            sf_description: "",
            sf_status: "",
        },
    });

    // Sync your formData to RHF for validation
    useEffect(() => {
        if (!formData) return;
        Object.entries(formData).forEach(([k, v]) => setValue(k, v, { shouldValidate: false, shouldDirty: false }));
    }, [formData, setValue]);

    // Map RHF errors to your formErrors for UI
    useEffect(() => {
        const mapped = {};
        Object.entries(rhfErrors || {}).forEach(([k, v]) => {
            mapped[k] = v?.message;
        });
        setFormErrors(mapped);
    }, [rhfErrors]);

    useEffect(() => {
        const fetchUserData = async () => {
            if (isAddStaff) return;

            if (!id || isNaN(id)) {
                navigate(parentRoute, { state: location.state });
                return;
            }
            const userId = id;

            setIsLoading(true);
            try {
                const { data: userData } = await getStaffById(userId);
                if (userData) {
                    setFormData({
                        id: userData.id,
                        sf_full_name: userData.full_name,
                        sf_username: userData.username,
                        sf_start_working_date: userData.start_working_date,
                        sf_role: userData.role?.id,
                        sf_description: userData.description ?? "",
                        sf_status: userData.status?.id
                    });
                }
            } catch (error) {
                toast.error("Failed to find user data");
                navigate(parentRoute, { state: location.state });
            } finally {
                setIsLoading(false);
            }
        };

        fetchUserData();
    }, [location.pathname, isAddStaff, navigate]);

    const handleChange = useCallback((e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    }, []);

    const handleSubmit = useCallback(async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setFormErrors({});
        try {
            let passData = removeEmptyFields(formData)
            await staffSchema.validate(passData, { abortEarly: false });
            if (isAddStaff) {
                const res = await createStaff(passData);
                Swal.fire({
                    title: "Staff added successfully!",
                    text: "Do you want to set a password for this user now?",
                    icon: "question",
                    showCancelButton: true,
                    confirmButtonText: "Yes, change password",
                    cancelButtonText: "No, later"
                }).then((result) => {
                    if (result.isConfirmed) {
                        navigate(`${parentRoute}/${res?.data?.id}`, { state: { ...location.state } });
                    } else {
                        navigate(parentRoute, { state: location.state });
                    }
                });
            } else {
                await updateStaff(formData.id, passData);
                toast.success("Staff updated successfully");
            }
        } catch (err) {
            if (err.name === 'ValidationError') {
                const validationErrors = {};
                err.errors.forEach((error) => {
                    const [key, message] = error.split(': ');
                    validationErrors[key] = message;
                });
                setFormErrors(validationErrors);
            } else {
                setFormErrors(err?.data?.errors || {});
            }
            toast.error(err.message || (isAddStaff ? "Failed to create staff" : "Failed to update staff"));
        } finally {
            setStaffs([])
            setIsLoading(false);
        }
    }, [formData, navigate, isAddStaff]);


    const handlePasswordChange = useCallback(async (e) => {
        e.preventDefault();
        if (passwordFormData.password !== passwordFormData?.password_confirmation) {
            toast.error("Passwords do not match");
            return;
        }
        setIsLoading(true);
        try {
            await adminResetPassword(formData.id, passwordFormData);
            toast.success("Password updated successfully");
        } catch (err) {
            toast.error(err.data?.message || "Failed to update password");
        } finally {
            setIsLoading(false);
        }
    }, [formData, passwordFormData]);

    const handleValidatedSubmit = useCallback(
        async (e) => {
            e.preventDefault();
            setFormErrors({});
            const valid = await trigger();
            if (!valid) {
                toast.error("Please fix the validation errors before submitting.");
                return;
            }
            await handleSubmit(e); // your original submit logic
        },
        [trigger, handleSubmit]
    );

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <MDBox pt={4} pb={3}>
                <Grid container spacing={6}>
                    <Grid item xs={12}>
                        <Card >
                            <HeaderCard
                                title={isAddStaff ? "Create New Staff" : "Edit Staff"}
                                showBackButton={true}
                                onBackClick={() => { navigate(parentRoute, { state: location.state }); setPrevSelectedRow(id, parentRoute) }}
                            />
                            <UserForm
                                formData={formData}
                                handleChange={handleChange}
                                handleSubmit={handleValidatedSubmit}
                                isEdit={!isAddStaff}
                                isLoading={isLoading}
                                buttonText={isAddStaff ? "Submit" : "Update"}
                                formErrors={formErrors}
                            />
                        </Card>
                        {!isAddStaff && (
                            <Card sx={{ mt: 8 }}>
                                <HeaderCard
                                    title={"Change Password"}
                                />
                                <MDBox pt={3} px={2} pb={2}>
                                    <form onSubmit={handlePasswordChange}>
                                        <Grid item xs={12}>
                                            <Grid container spacing={3}>
                                                <Grid item xs={12} md={6}>
                                                    <MDInput
                                                        fullWidth
                                                        label="New Password"
                                                        name="new_password"
                                                        type="password"
                                                        inputProps={{ minLength: 6 }}
                                                        value={passwordFormData?.password || ''}
                                                        onChange={(e) => setPasswordFormData({ ...passwordFormData, password: e.target.value })}
                                                        required={!!passwordFormData?.password_confirmation}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} md={6}>
                                                    <MDInput
                                                        fullWidth
                                                        label="Confirm New Password"
                                                        name="confirm_password"
                                                        type="password"
                                                        inputProps={{ minLength: 6 }}
                                                        value={passwordFormData?.password_confirmation || ''}
                                                        onChange={(e) => setPasswordFormData({ ...passwordFormData, password_confirmation: e.target.value })}
                                                        required={!!passwordFormData?.password}
                                                    />
                                                </Grid>
                                                <Grid item xs={12}>
                                                    <MDBox display="flex" justifyContent="flex-end">
                                                        <MDButton
                                                            variant="contained"
                                                            color="primary"
                                                            type="submit"
                                                            loading={isLoading}
                                                            disabled={!passwordFormData?.password || !passwordFormData?.password_confirmation}
                                                        >
                                                            Change Password
                                                        </MDButton>
                                                    </MDBox>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    </form>
                                </MDBox>
                            </Card>
                        )}
                    </Grid>
                </Grid>
            </MDBox>
        </DashboardLayout>
    );
};

export default AddStaff;
